import {
    DataGridPremium,
    useGridApiRef,
    type GridDataSource as GridDataSourcePremium,
} from '@mui/x-data-grid-premium';
import {
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
    forwardRef,
    useMemo,
} from 'react';
import {
    configureDataSourceAndFields,
    handleDiscardChanges,
    handleGetRowClassName,
    handleGetSubRowClassName,
    handleProcessRowUpdate,
    styleEditedRow,
} from '../../utils/BulkEditHelper.tsx';
import { type GridColDef, type DataGridProps } from '@mui/x-data-grid';
import { type Theme } from '@mui/system';
import { Paper, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import './GridRowEditableDetail.css';

import FieldsNames from '../../defaults/FieldNames.json';
export interface GridRowEditableDetailHandle {
    saveChanges: () => Promise<void>;
    discardChanges: () => void;
}

import BulkEditActionsCell from '../BulkEditActionsCell/BulkEditActionsCell.tsx';
import { type UnsavedChanges } from '../../utils/BulkEditHelper.tsx';
import FundingDialog from '../FundingDialog/FundingDialog.tsx';
import { useDispatch, useSelector } from 'react-redux';
import FundingService from '../../services/FundingService';
import { deriveParentAttributesFromFundingRows } from '../../utils/FundingHelper';
import { isFeaturePostFire } from '../../utils/FeatureHelper';
import { setFilterActionsDisabled } from '../../redux/slices/globalContextSlice.jsx';
import ErrorService from '../../services/ErrorService';
import AuditLogService from '../../services/AuditLogService';
import FieldNames from '../../defaults/FieldNames.json';
import {
    fundingGridAggregationModel,
    fundingGridAggregationFunctions,
} from '../../defaults/BulkEditConfiguration.tsx';
import {
    setParentRowEditedCells,
    setSubRowEditedCells,
} from '../../redux/slices/displayStateSlice.jsx';
import type {RootState} from "../../redux/store.tsx";

const GridRowEditableDetail = forwardRef<GridRowEditableDetailHandle, any>(
    (
        {
            joinId,
            joinFeature,
            fields,
            layerId,
            joinIdField,
            joinFeatureField,
            parentFeature,
            onParentFeatureUpdate,
            parentRowId,
            initialUnsavedChanges,
            onUnsavedChangesSync,
        },
        ref
    ) => {
        const dispatch = useDispatch();

        const subRowEditedCells = useSelector((state: RootState) => state.displayState.subRowEditedCells);

        // boilerplate setup code for bulk editing from docs
        const apiRef = useGridApiRef();
        const [dataSource, setDataSource] = useState<GridDataSourcePremium | undefined>(undefined);
        const gridColumns = useRef<GridColDef[]>([]);
        const userInfo = useSelector((state: RootState) => state.globalContext.userInfo);
        const parentRowEditedCells = useSelector((state: RootState) => state.displayState.parentRowEditedCells);

        const unsavedChangesRef = useRef<UnsavedChanges>(
            initialUnsavedChanges ?? {
                unsavedRows: {},
                rowsBeforeChange: {},
            }
        );
        const [_hasUnsavedRows, setHasUnsavedRows] = useState(
            initialUnsavedChanges
                ? Object.keys(initialUnsavedChanges.unsavedRows).length > 0
                : false
        );
        const [isSaving, setIsSaving] = useState(false);

        /**
         * Whenever unsaved changes are modified, sync them up to the parent
         * so they survive unmount (panel collapse).
         */
        const syncToParent = useCallback(() => {
            if (onUnsavedChangesSync && parentRowId != null) {
                onUnsavedChangesSync(parentRowId, {
                    unsavedRows: { ...unsavedChangesRef.current.unsavedRows },
                    rowsBeforeChange: { ...unsavedChangesRef.current.rowsBeforeChange },
                });
            }
        }, [onUnsavedChangesSync, parentRowId]);

        // Sync lifted state on unmount so collapse preserves pending changes
        useEffect(() => {
            return () => {
                syncToParent();
            };
        }, [syncToParent]);

        // ── Funding Dialog state ─────────────────────────────────────────────
        const [fundingDialogOpen, setFundingDialogOpen] = useState(false);
        const [fundingDialogRow, setFundingDialogRow] = useState<any>(null);

        const openFundingDialog = useCallback((row: any) => {
            setFundingDialogRow(row);
            setFundingDialogOpen(true);
        }, []);

        const closeFundingDialog = useCallback(() => {
            setFundingDialogOpen(false);
            setFundingDialogRow(null);
        }, []);

        const handleFundingDialogSave = (mergedValues: Record<string, any>[]) => {
            // 1. Apply each updated funding row to the detail grid's unsaved changes
            mergedValues.forEach((row) => {
                if (row?.rowId) {
                    const rowId = row.rowId;
                    // Snapshot the old row BEFORE any mutation
                    const oldRow = { ...apiRef.current?.getRow(rowId) };

                    processRowUpdate(row, oldRow, { rowId: rowId });
                    // Push the full updated row into the grid so it renders the new values
                    apiRef.current?.updateRows([row]);
                }
            });

            // 2. Derive parent feature attribute changes from the full set of funding rows
            //    Merge the just-saved rows on top of the current sibling snapshot so
            //    the derivation sees the latest state of every row.
            const updatedRowMap = new Map(
                mergedValues.filter((r) => r?.rowId).map((r) => [r.rowId, r])
            );
            const allCurrentRows = siblingRowsRef?.current.map((r) =>
                updatedRowMap.has(r.rowId) ? updatedRowMap.get(r.rowId) : r
            );


            // Set changes in the sibling rows to the store so they are available
            // to cross check for cell styling
            dispatch(
                setSubRowEditedCells({
                    existingRow: subRowEditedCells.existingRow,
                    newRow: [...allCurrentRows],
                    changesSavedToParent: false,
                })
            );

            const parentUpdates = deriveParentAttributesFromFundingRows(
                allCurrentRows,
                parentFeature,
            );

            // Set changes in the parent row to the store so it is available
            // to cross check for cell styling
            dispatch(
                setParentRowEditedCells({
                    existingRow: parentRowEditedCells.existingRow,
                    newRow: structuredClone(parentUpdates),
                })
            );

            if (onParentFeatureUpdate && Object.keys(parentUpdates).length > 0) {
                onParentFeatureUpdate(parentRowId, parentUpdates);
            }

            closeFundingDialog();
            dispatch(setFilterActionsDisabled(true));

            // Re-fetch to recompute server-side aggregation footer with
            // the unsaved local edits merged in.
            // skipCache bypasses the DataGrid's internal data source cache
            // so getRows is actually called again with the latest unsaved changes.
            apiRef.current?.dataSource.fetchRows(undefined, { skipCache: true });
        };

        /** Collect sibling rows for the FundingDialog (BIL/BAR detection). */
        const siblingRowsRef = useRef<any[]>([]);

        useEffect(() => {
            if (!apiRef.current) return;

            const unsubscribe = apiRef.current.subscribeEvent('rowsSet', () => {
                const rows = Array.from(apiRef.current.getRowModels().values());
                if (rows.length > 0) {
                    siblingRowsRef.current = rows;
                }
            });

            return () => unsubscribe();
        }, [apiRef]);

        /**
         * After the data source loads rows, re-apply any restored unsaved edits
         * so the grid displays them with the correct styling.
         */
        const hasReapplied = useRef(false);
        useEffect(() => {
            if (!apiRef.current || !initialUnsavedChanges || hasReapplied.current) return;

            const unsavedRows = initialUnsavedChanges.unsavedRows;
            if (Object.keys(unsavedRows).length === 0) return;

            const unsubscribe = apiRef.current.subscribeEvent('rowsSet', () => {
                if (hasReapplied.current) return;
                // Only apply once the grid actually has rows loaded
                const currentRows = apiRef.current.getRowModels();
                if (currentRows.size === 0) return;

                hasReapplied.current = true;
                Object.values(unsavedRows).forEach((row: any) => {
                    apiRef.current?.updateRows([row]);
                });
            });

            return () => unsubscribe();
        }, [apiRef, initialUnsavedChanges]);

        const processRowUpdate = useCallback<NonNullable<DataGridProps['processRowUpdate']>>(
            (newRow, oldRow) => {
                const result = handleProcessRowUpdate(newRow, oldRow, unsavedChangesRef, setHasUnsavedRows, () => {});
                syncToParent();
                return result;
            },
            [syncToParent]
        );

        const discardChanges = useCallback(() => {
             handleDiscardChanges(unsavedChangesRef, setHasUnsavedRows, apiRef);
            syncToParent();
            // Recompute aggregation footer now that unsaved changes are cleared
            apiRef.current?.dataSource.fetchRows(undefined, { skipCache: true });
        }, [apiRef, syncToParent]);

        const bulkEditingContextValue = useMemo(
            () => ({
                unsavedChangesRef,
                setHasUnsavedRows,
            }),
            []
        );

        const saveChanges = useCallback(async () => {
            try {
                setIsSaving(true);

                const saveRows: any = [];
                Object.values(unsavedChangesRef.current.unsavedRows).forEach((row) => {
                    // shallow copy cause we will remove rowId
                    const attrs = Object.assign({}, { ...row });
                    delete attrs.rowId;
                    saveRows.push(attrs);
                });
                FundingService.updateFundingEntries(saveRows)
                    .then(() => {
                        setIsSaving(false);
                        setHasUnsavedRows(false);
                        unsavedChangesRef.current = {
                            unsavedRows: {},
                            rowsBeforeChange: {},
                        };
                    })
                    .catch((error) => {
                        ErrorService.addErrorLogEntry(
                            error
                        );
                    })
                    .finally(() => {
                        saveRows.forEach((row) => {
                            const changeString = Object.entries(row)
                                .map(([key, value]) => `${key}: ${value}`)
                                .join(', ');

                            AuditLogService.addAuditLogEntry(
                                row,
                                row[FieldNames.featureFields.joinID],
                                row[FieldNames.featureFields.joinFeature],
                                userInfo[FieldNames.userFields.nifcUsername],
                                `Funding Row Modified: ${changeString}`
                            );
                        });
                        setIsSaving(false);
                    });
            } catch (error) {
                setIsSaving(false);
            }
        }, [apiRef, joinId]);

        useImperativeHandle(ref, () => ({
            saveChanges,
            discardChanges,
        }));

        const getRowClassName = useCallback<NonNullable<DataGridProps['getRowClassName']>>(
            ({ id }) => {
                return handleGetRowClassName(id, unsavedChangesRef);
            },
            []
        );

        const getCellClassName = ({ id, field }) => {
            return handleGetSubRowClassName(id, field, subRowEditedCells);
        };

        useEffect(() => {
            if (joinId && joinFeature && parentFeature) {
                const initSetup = async () => {
                    const { dataSource, colDefs } = await configureDataSourceAndFields(
                        layerId,
                        fields,
                        `${joinIdField} = ${joinId} AND ${joinFeatureField} = '${joinFeature}'`,
                        FieldsNames.fundingFields.objectID,
                        unsavedChangesRef
                    );
                    const cls = parentFeature?.[FieldNames.featureFields.class];
                    // Derive if feature is 'Actual', to decouple BulkEdit tree from Details tree via redux 'store.details.isActual'
                    const isParentActual = cls === FieldNames.classTypes.actualTreatment || cls === FieldNames.classTypes.actualActivity;
                    gridColumns.current = [
                        {
                            field: 'actions',
                            type: 'actions',
                            width: 100,
                            renderCell: (params: any) => {
                                if (
                                    params.row[FieldNames.featureFields.joinID] != null &&
                                    params.row[FieldNames.fundingFields.joinFeature] != null
                                ) {
                                    return (
                                        <BulkEditActionsCell
                                            {...params}
                                            context={bulkEditingContextValue}
                                            showRestoreAction={false}
                                            extraActions={[
                                                {
                                                    icon: <EditIcon />,
                                                    label: 'Edit Funding Assignment',
                                                    onClick: () => openFundingDialog(params.row),
                                                    disabled: isParentActual,
                                                    tooltipText: isParentActual
                                                        ? 'Editing is disabled for actual treatments or activities'
                                                        : 'Edit Funding Assignment',
                                                },
                                            ]}
                                        />
                                    );
                                } else return <></>
                            },
                        },
                        ...colDefs,
                    ];
                    setDataSource(dataSource);
                };
                initSetup();
            }
        }, [joinId, joinFeature, bulkEditingContextValue, openFundingDialog]);

        return (
            <Paper className='grid-detail-panel'>
                <Typography>
                    <h4>Funding for {parentFeature[FieldNames.featureFields.name]}</h4>
                </Typography>
                <DataGridPremium
                    columns={gridColumns.current}
                    dataSource={dataSource}
                    apiRef={apiRef}
                    getRowId={(row) => row.rowId}
                    sx={{
                        [`.row--edited`]: {
                            backgroundColor: (theme: Theme) => {
                                return styleEditedRow(theme);
                            },
                        },
                        [`.cell--edited`]: {
                            border: '1px dashed #ff8500db',
                        },
                    }}
                    loading={isSaving}
                    getRowClassName={getRowClassName}
                    getCellClassName={getCellClassName}
                    disableRowSelectionOnClick
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={console.error}
                    onDataSourceError={console.error}
                    disableColumnSelector
                    disableColumnFilter
                    aggregationModel={fundingGridAggregationModel}
                    aggregationFunctions={fundingGridAggregationFunctions}
                    hideFooter
                    columnVisibilityModel={{
                        [FieldNames.fundingFields.equipmentCost]: isFeaturePostFire(parentFeature),
                        [FieldNames.fundingFields.materialAndSupplyCost]: isFeaturePostFire(parentFeature),
                        [FieldNames.fundingFields.procurementCost]: isFeaturePostFire(parentFeature),
                        [FieldNames.fundingFields.travelCost]: isFeaturePostFire(parentFeature),
                    }}
                />

                <FundingDialog
                    open={fundingDialogOpen}
                    selectedRow={fundingDialogRow}
                    parentFeature={parentFeature}
                    siblingRows={siblingRowsRef.current}
                    rowIdField="rowId"
                    onClose={closeFundingDialog}
                    onSave={handleFundingDialogSave}
                />
            </Paper>
        );
    }
);

export default GridRowEditableDetail;
